/**
 * B549 — group bookings, leg (1): the inquiry door.
 *
 * "More than 20 is a group booking; the groups team will book those"
 * (Pathik 2026-09-24). The buyer surfaces stop the stepper at the
 * channel's `groupThreshold` and offer the interrupt; this operation is
 * where the interrupt's form lands — organisation, contact, preferred
 * date, headcount, kind — as a row the groups desk will read. A guest
 * session reaches it (the interrupt lives in the booking sheet, where a
 * guest token already exists), and STAFF may type one in from a call.
 *
 * Deliberately a WRITE op, not a public door: the clientKey makes a
 * double-pressed SEND one inquiry (Inv 4), and the guest token keeps the
 * public-door abuse surface unchanged.
 */
import { z } from "zod";
import { readOp, writeOp, zEpochMs, zId } from "./common.js";

export const GROUP_INQUIRY_KINDS = ["school", "corporate", "tour", "other"] as const;

/** B549 (4a): the desk's stages — the queue's pills. GROWS, never renames. */
export const GROUP_STAGES = ["NEW", "QUOTED", "TENTATIVE", "CONFIRMED"] as const;
export const GROUP_TICKETING = ["PER_SEAT", "GROUP_TICKET"] as const;
export const GROUP_QUOTE_COLLECT = ["HOSTED_INVOICE", "PAY_PAGE", "BANK_TRANSFER"] as const;

const zDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const zCount = z.number().int().min(0);
const zMinor = z.number().int().min(0);

/** Ruled (c): the desk's own deposit terms — never a platform default. */
const zDeposit = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("NONE") }).strict(),
  z.object({ kind: z.literal("PERCENT"), percent: z.number().int().min(1).max(100) }).strict(),
  z.object({ kind: z.literal("AMOUNT"), amountMinor: z.number().int().min(1) }).strict(),
]);
const zBalance = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("ON_ACCEPTANCE") }).strict(),
  z.object({ kind: z.literal("DAYS_BEFORE_VISIT"), days: z.number().int().min(0).max(365) }).strict(),
  z.object({ kind: z.literal("NET_AFTER_VISIT"), days: z.number().int().min(0).max(365) }).strict(),
]);
const zTerms = z
  .object({
    deposit: zDeposit,
    depositDueAt: zEpochMs.optional(),
    balance: zBalance,
    expiresAt: zEpochMs,
    collectVia: z.enum(GROUP_QUOTE_COLLECT),
  })
  .strict();

/** A quote line as the desk names it: WHAT and HOW MANY — never a price. */
const zQuoteLineIn = z.object({ productId: zId, variantId: zId, qty: z.number().int().min(1).max(100_000) }).strict();

const zQuoteLine = z
  .object({
    productId: zId,
    variantId: zId,
    productName: z.string().min(1),
    variantName: z.string().min(1),
    qty: z.number().int().min(1),
    groupUnitMinor: zMinor,
    publicUnitMinor: zMinor.optional(),
  })
  .strict();

const zQuote = z
  .object({
    status: z.enum(["DRAFT", "SENT"]),
    channelId: zId,
    slotId: zId,
    poolId: zId,
    visitAt: zEpochMs,
    ticketing: z.enum(GROUP_TICKETING),
    headcount: z.number().int().min(1),
    chaperones: zCount,
    currency: z.string().min(3).max(3),
    lines: z.array(zQuoteLine),
    seats: z.number().int().min(1),
    totalMinor: zMinor,
    publicTotalMinor: zMinor.optional(),
    terms: zTerms,
    depositMinor: zMinor,
    balanceDueAt: zEpochMs.optional(),
    allocationId: zId.optional(),
    heldSeats: zCount.optional(),
    sentAt: zEpochMs.optional(),
    savedAt: zEpochMs,
  })
  .strict();

/** The queue row's glance at the quote: VISIT and VALUE columns (438:2). */
const zQuoteGlance = z
  .object({
    status: z.enum(["DRAFT", "SENT"]),
    visitAt: zEpochMs,
    seats: z.number().int().min(1),
    totalMinor: zMinor,
    currency: z.string().min(3).max(3),
    /** Seats the quote's carve-out holds, once sent — the KPI's "seats held". */
    heldSeats: zCount.optional(),
    /** The option date: held seats return to sale here. */
    expiresAt: zEpochMs,
  })
  .strict();

export const groupInquiry = writeOp({
  name: "group.inquiry",
  permission: "order:write",
  principals: ["CUSTOMER", "STAFF"],
  fields: {
    channelId: zId,
    organisation: z.string().min(1).max(200),
    contactName: z.string().min(1).max(120),
    contactEmail: z.string().email().max(254),
    contactPhone: z.string().min(3).max(40).optional(),
    /** The day they have in mind, if any — a calendar date, not a slot. */
    preferredDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    headcount: z.number().int().min(1).max(100_000),
    kind: z.enum(GROUP_INQUIRY_KINDS),
    notes: z.string().max(2000).optional(),
  },
  response: z.object({ inquiryId: zId, receivedAt: z.number().int() }).strict(),
  exampleRequest: {
    channelId: "web",
    organisation: "Lakeview Elementary",
    contactName: "Dana Field",
    contactEmail: "dana@lakeview.example",
    headcount: 25,
    kind: "school",
  },
  exampleResponse: { inquiryId: "gi-1", receivedAt: 1_726_000_000_000 },
});

/**
 * B549 leg (3) — the desk's queue read: every inquiry of the tenant,
 * newest first. MANAGER/ADMIN back office (the Bookings audience); the
 * lifecycle beyond NEW (quotes, tentative holds, invoices) is its own
 * leg, so the row's status vocabulary is deliberately closed at NEW here
 * and will GROW, never rename.
 */
export const groupInquiryList = readOp({
  name: "group.inquiry.list",
  permission: "order:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {},
  response: z
    .object({
      inquiries: z.array(
        z
          .object({
            inquiryId: zId,
            channelId: zId,
            organisation: z.string().min(1),
            contactName: z.string().min(1),
            contactEmail: z.string().min(1),
            contactPhone: z.string().min(1).optional(),
            preferredDate: z
              .string()
              .regex(/^\d{4}-\d{2}-\d{2}$/)
              .optional(),
            headcount: z.number().int().min(1),
            kind: z.enum(GROUP_INQUIRY_KINDS),
            notes: z.string().optional(),
            // B549 (4a): the vocabulary grew past NEW — additive, the leg-3 promise
            status: z.enum(GROUP_STAGES),
            createdAt: z.number().int(),
            quote: zQuoteGlance.optional(),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    inquiries: [
      {
        inquiryId: "gi-1",
        channelId: "web",
        organisation: "Lakeview Elementary",
        contactName: "Dana Field",
        contactEmail: "dana@lakeview.example",
        headcount: 25,
        kind: "school",
        status: "NEW",
        createdAt: 1_726_000_000_000,
      },
    ],
  },
});

const zInquiryFull = z
  .object({
    inquiryId: zId,
    channelId: zId,
    organisation: z.string().min(1),
    contactName: z.string().min(1),
    contactEmail: z.string().min(1),
    contactPhone: z.string().min(1).optional(),
    preferredDate: zDate.optional(),
    headcount: z.number().int().min(1),
    kind: z.enum(GROUP_INQUIRY_KINDS),
    notes: z.string().optional(),
    status: z.enum(GROUP_STAGES),
    createdAt: z.number().int(),
    revision: zCount,
    quote: zQuote.optional(),
  })
  .strict();

const EXAMPLE_QUOTE = {
  status: "DRAFT" as const,
  channelId: "groups",
  slotId: "harbor#2026-10-14T10:30",
  poolId: "pool-main",
  visitAt: 1_792_000_000_000,
  ticketing: "PER_SEAT" as const,
  headcount: 54,
  chaperones: 6,
  currency: "USD",
  lines: [{ productId: "harbor", variantId: "student", productName: "Harbor cruise", variantName: "Student", qty: 60, groupUnitMinor: 1850, publicUnitMinor: 2400 }],
  seats: 60,
  totalMinor: 111_000,
  publicTotalMinor: 144_000,
  terms: { deposit: { kind: "PERCENT" as const, percent: 25 }, balance: { kind: "DAYS_BEFORE_VISIT" as const, days: 14 }, expiresAt: 1_791_000_000_000, collectVia: "HOSTED_INVOICE" as const },
  depositMinor: 27_750,
  balanceDueAt: 1_790_790_400_000,
  savedAt: 1_790_000_000_000,
};

/**
 * B549 (4a) — one inquiry with its quote, for the quote builder (441:2) and
 * the booking detail (443:2). `pool` is the allocation card's figures for
 * the quote's pool (absent before a quote names one): capacity, sold, held,
 * and the seats OTHER live quotes hold there. Absent inquiry = 404 (a
 * foreign tenant's id answers exactly like a missing one, Inv 10).
 */
export const groupInquiryGet = readOp({
  name: "group.inquiry.get",
  permission: "order:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId },
  response: z
    .object({
      inquiry: zInquiryFull,
      pool: z.object({ capacity: zCount, sold: zCount, held: zCount, heldByOtherQuotes: zCount }).strict().optional(),
    })
    .strict(),
  exampleRequest: { inquiryId: "gi-1" },
  exampleResponse: {
    inquiry: {
      inquiryId: "gi-1",
      channelId: "web",
      organisation: "Lincoln Elementary",
      contactName: "Ana Ruiz",
      contactEmail: "ana@lincoln.example",
      headcount: 60,
      kind: "school",
      status: "NEW",
      createdAt: 1_789_900_000_000,
      revision: 1,
      quote: EXAMPLE_QUOTE,
    },
    pool: { capacity: 200, sold: 0, held: 6, heldByOtherQuotes: 6 },
  },
});

/**
 * B549 (4a) — SAVE DRAFT on 441:2. The desk names the selling channel, the
 * departure and pool, the ticketing mode, the party and the lines (what and
 * how many — the SERVER prices them off the channel's list), and the terms.
 * `revision` is the one the desk read: a stale one is a 409, never an
 * overwrite. The stage does not move; a revision of a sent quote is a draft
 * until re-sent, its seats still held.
 */
export const groupQuoteSave = writeOp({
  name: "group.quote.save",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    inquiryId: zId,
    revision: zCount,
    channelId: zId,
    slotId: zId,
    poolId: zId,
    ticketing: z.enum(GROUP_TICKETING),
    headcount: z.number().int().min(1).max(100_000),
    chaperones: z.number().int().min(0).max(100_000),
    lines: z.array(zQuoteLineIn).min(1).max(20),
    terms: zTerms,
  },
  response: z.object({ inquiryId: zId, revision: zCount, quote: zQuote }).strict(),
  exampleRequest: {
    inquiryId: "gi-1",
    revision: 0,
    channelId: "groups",
    slotId: "harbor#2026-10-14T10:30",
    poolId: "pool-main",
    ticketing: "PER_SEAT",
    headcount: 54,
    chaperones: 6,
    lines: [{ productId: "harbor", variantId: "student", qty: 60 }],
    terms: { deposit: { kind: "PERCENT", percent: 25 }, balance: { kind: "DAYS_BEFORE_VISIT", days: 14 }, expiresAt: 1_791_000_000_000, collectVia: "HOSTED_INVOICE" },
  },
  exampleResponse: { inquiryId: "gi-1", revision: 1, quote: EXAMPLE_QUOTE },
});

/**
 * B549 (4a) — SEND QUOTE: the saved draft's seats are held as an allocation
 * carve-out on its pool until the quote expires (the release-back sweep
 * returns them if it lapses), and the inquiry becomes QUOTED. Not enough
 * seats = 409 and nothing moves. The quote email + hosted accept page are
 * their own leg (447:2); this door is the hold and the stage.
 */
export const groupQuoteSend = writeOp({
  name: "group.quote.send",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId, revision: zCount },
  response: z
    .object({ inquiryId: zId, revision: zCount, status: z.enum(GROUP_STAGES), allocationId: zId, heldSeats: z.number().int().min(1), expiresAt: zEpochMs })
    .strict(),
  exampleRequest: { inquiryId: "gi-1", revision: 1 },
  exampleResponse: { inquiryId: "gi-1", revision: 2, status: "QUOTED", allocationId: "gq-gi-1-r1", heldSeats: 60, expiresAt: 1_791_000_000_000 },
});
